// Deterministic, compliant physician-query draft for documentation-gap
// candidates. Pure module: no component imports, no side effects.
//
// Compliance shape (ACDIS/AHIMA compliant query practice): the draft carries
// the clinical indicator (verbatim evidence with its source), asks a neutral
// clarification question derived from the candidate's missing_fact, and
// offers multiple reasonable options, always including "Other" and
// "Clinically unable to determine". It never mentions reimbursement, PDGM,
// payment, revenue, billing, or dollar amounts. The builder adds none of
// those terms itself; missing_fact and evidence are backend-authored clinical
// text that never contains payment language (the pipeline keeps PDGM notes in
// separate validation fields, not in gap prose).

const STANDING_OPTIONS = [
    '[ ] Other (please specify): ____________________',
    '[ ] Clinically unable to determine',
];

// House style bars em dashes anywhere in the draft; backend prose sometimes
// uses them, so normalize to a plain hyphen without changing meaning.
function normalizeDashes(text) {
    return text.replace(/—/g, '-');
}

// Strip a leading "Verification: " framing the verify pass prepends to some
// gap facts; the remainder already names the missing fact precisely.
function questionFromMissingFact(missingFact) {
    return (missingFact || '').replace(/^\s*Verification:\s*/i, '').trim();
}

/**
 * Build a plain-text physician query draft for a documentation-gap candidate.
 *
 * @param {object} candidate - gap candidate: { missing_fact, evidence,
 *   document, page, query_options } (query_options may be absent or empty;
 *   only the two standing options render then).
 * @param {object} [caseMeta] - optional case context. Supported key:
 *   patientName (string). With caseMeta = {} (or omitted) the patient line
 *   renders as a blank fill-in, so the builder never needs case data.
 * @returns {string} the draft, ready for the clipboard.
 */
export function buildPhysicianQuery(candidate, caseMeta = {}) {
    const lines = [];

    lines.push('Physician Query');
    lines.push('');
    lines.push(`Patient: ${caseMeta.patientName || '____________________'}`);
    lines.push('');
    lines.push(
        'We are requesting clarification of the condition documented below. ' +
        'Please respond based on your medical judgment and the clinical facts ' +
        'of this patient.'
    );
    lines.push('');

    if (candidate.evidence) {
        lines.push('Clinical indicator from the record:');
        lines.push(`"${candidate.evidence}"`);
        const source = candidate.document
            ? `Source: ${candidate.document}${candidate.page ? `, page ${candidate.page}` : ''}`
            : null;
        if (source) lines.push(source);
        lines.push('');
    }

    const question = questionFromMissingFact(candidate.missing_fact);
    if (question) {
        lines.push(`Please clarify: ${question}`);
        lines.push('');
    }

    lines.push('Please check the option that best reflects your clinical assessment:');
    for (const option of candidate.query_options || []) {
        lines.push(`[ ] ${option}`);
    }
    lines.push(...STANDING_OPTIONS);
    lines.push('');
    lines.push('Physician signature: ____________________');
    lines.push('Date: ____________________');

    return normalizeDashes(lines.join('\n'));
}
